import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';

let initialized = false;

const SESSION_REPLAY_CONFIG = {
  useWebWorker: true,
  privacyConfig: { defaultMaskLevel: 'medium' },
};

const DEFAULT_SESSION_REPLAY_SAMPLE_RATE = 0.1;

export function initAmplitude({ sessionReplaySampleRate } = {}) {
  if (initialized) return;

  const apiKey = window.chatwootConfig?.amplitudeKey;
  if (!apiKey) return;

  const storedDeviceId = localStorage.getItem('cw_amplitude_device_id');
  const deviceId =
    storedDeviceId ||
    (window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

  localStorage.setItem('cw_amplitude_device_id', deviceId);
  amplitude.setDeviceId(deviceId);

  amplitude.add(
    sessionReplayPlugin({
      ...SESSION_REPLAY_CONFIG,
      sampleRate: sessionReplaySampleRate ?? DEFAULT_SESSION_REPLAY_SAMPLE_RATE,
    })
  );

  amplitude.add(
    engagementPlugin({
      locale: window.chatwootConfig?.selectedLocale,
    })
  );

  amplitude.init(apiKey, {
    autocapture: {
      attribution: true,
      pageViews: true,
      sessions: true,
      formInteractions: true,
      fileDownloads: true,
      pageUrlEnrichment: true,
      elementInteractions: true,
      frustrationInteractions: true,
      webVitals: false,
    },
  });

  initialized = true;
}

export function trackEvent(name, props = {}) {
  if (!initialized) return;
  amplitude.track(name, props);
}

export function resetAmplitude() {
  amplitude.reset();
  initialized = false;
  localStorage.removeItem('cw_amplitude_device_id');
}

export function setAmplitudeUser(id) {
  // Guard consistent with trackEvent / setAmplitudeUserProperties / resetAmplitude:
  // calling setUserId on an SDK that never initialized (no amplitudeKey, init
  // failed, dev env without the key) would queue a side effect against an
  // uninitialized client and could produce out-of-order identify events on
  // fast login/logout cycles.
  if (!initialized) return;
  amplitude.setUserId(id);
}

/**
 * Sets user properties on the current Amplitude user. Properties persist
 * across sessions (they live on the user, not on individual events) — useful
 * for cohort filtering like "adoption on instance3" or "agents on Account #14".
 * Undefined and null values are skipped so we don't poison the user profile
 * when a field is unavailable (e.g. instance unknown during boot).
 */
export function setAmplitudeUserProperties(props) {
  if (!initialized) return;
  const identifyEvent = new amplitude.Identify();
  Object.entries(props).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      identifyEvent.set(key, value);
    }
  });
  amplitude.identify(identifyEvent);
}

// Sets the tenant dimension on the Amplitude user so events can be grouped by
// account natively (account_id is otherwise only inferrable from the page URL).
// Narrower API kept alongside setAmplitudeUserProperties for callers that only
// have account in scope (e.g. NewHook.vue post-create) — both delegate to
// amplitude.identify under the hood, no behavioural divergence.
export function setAmplitudeAccount({ id, name } = {}) {
  if (!initialized || !id) return;

  const identify = new amplitude.Identify()
    .set('account_id', id)
    .set('account_name', name ?? '');
  amplitude.identify(identify);
}

